import React, { useEffect, useState } from "react";
import AttachmentActionsMenu from "./attachment-actions-menu";
import FilePreviewModal from "./file-preview-modal";
import UploadLinkUrlAttachment from "./upload-link-url-attachment";
import { deleteLeadAttachment } from "../../../../../../api/lead";
import ConfirmationPopup from "../../../../../../common/components/confirmation-popup/confirmation-popup";
import { Typography } from "@mui/material";

const normalizeExternalUrl = (url: string) => {
    const trimmedUrl = url?.trim();
    if (!trimmedUrl) return trimmedUrl;

    return /^https?:\/\//i.test(trimmedUrl)
        ? trimmedUrl
        : `https://${trimmedUrl}`;
};

interface AttachmentActionsProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    type: "LINK" | "FILE";
    item: any;
    fetchAttachments: () => void;
    setLoading: any;
    setAlertMessage: any;
    setAlertType: any;
    setAlertOpen: any;
    disableEdit?: boolean
}

const AttachmentActions: React.FC<AttachmentActionsProps> = ({
    open,
    anchorEl,
    onClose,
    type,
    item,
    fetchAttachments,
    setLoading,
    setAlertMessage,
    setAlertOpen,
    setAlertType,
    disableEdit,
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [isEditUrl, setIsEditUrl] = useState(false);
    const [isDeleteFile, setIsDeleteFile] = useState(false);
    const [leadData, setLeadData] = useState({});

    const handleViewFile = () => {
        setPreviewOpen(true);
    };

    //to close the view preview for file
    const handleClosePreview = () => {
        setPreviewOpen(false);
        onClose();
    };

    const handleViewLink = () => {
        const fileUrl = normalizeExternalUrl(item?.link);
        if (fileUrl) {
            window.open(fileUrl, "_blank", "noopener,noreferrer");
        } else {
            console.warn("No file URL found to view.");
        }
        onClose();
    };

    const handleView = () => {
        if (type === "FILE") handleViewFile();
        else if (type === "LINK") handleViewLink();
    };

    const handleDeleteClose = () => {
        setIsDeleteFile(false)
        onClose();
    }

    const handleDeleteFileApiCall = async () => {
        try {
            setLoading(true);
            const response = await deleteLeadAttachment(item?.lead_id, item.id);
            if (response?.status === 200) {
                setAlertMessage("Attachment archived successfully");
                setAlertType("success");
                fetchAttachments();
            }
        } catch (error: any) {
            setAlertMessage(
                error?.response?.data?.message || error?.response?.data?.error ||
                "Something went wrong. Please try again later."
            );
            setAlertType("error");
        } finally {
            setLoading(false);
            setAlertOpen(true);
            onClose()
        }
    };
    const handleDeleteFile = async () => {
        setIsDeleteFile(true);
    };

    const handleEditLink = () => {
        setIsEditUrl(true);
        setLeadData({
            link: item?.link,
            fileName: item?.file?.original_file_name,
            attachmentId: item?.id,
        });
    };

    const handleDownloadFile = () => {
        const fileUrl = normalizeExternalUrl(item?.link || item?.file?.url);
        const fileName = item?.file?.original_file_name || "download";
        if (fileUrl) {
            const link = document.createElement("a");
            link.href = fileUrl;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            onClose();
        } else {
            console.warn("No file URL found to download.");
        }
    };

    return (
        <>
            <AttachmentActionsMenu
                open={open}
                anchorEl={anchorEl}
                type={type}
                onClose={onClose}
                onView={handleView}
                onDelete={handleDeleteFile}
                onDownload={handleDownloadFile}
                onEdit={handleEditLink}
                disableEdit={disableEdit}
            />
            <FilePreviewModal
                open={previewOpen}
                onClose={handleClosePreview}
                fileUrl={item.link || item.file.url}
                fileName={item.file.original_file_name}
                id={item?.id}
                previewLink={item?.previewLink}
                size={item?.file?.size}
            />
            <UploadLinkUrlAttachment
                selectedOption={isEditUrl ? "linkUrl" : ""}
                leadId={item?.lead_id}
                fetchAttachments={fetchAttachments}
                setLoading={setLoading}
                leadData={leadData}
                setIsEditUrl={setIsEditUrl}
                //To close the view,edit,delete popup
                onClose={onClose}
            />
            <ConfirmationPopup
                title={`Archive ${type?.toLowerCase()}`}
                note={
                    <Typography variant="body2">
                        Are you sure you want to archive <strong>{item?.file?.original_file_name}</strong>?
                    </Typography>
                }
                open={isDeleteFile}
                buttonText="Archive"
                onOkay={handleDeleteFileApiCall}
                onCancel={handleDeleteClose}
            />

        </>
    );
};

export default AttachmentActions;
